import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, CreditCard, Calendar, Shield, Sparkles, Gift } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { API } from "../utils/constants";
import { Biz360Logo } from "../components/layout/Biz360Logo";
import { PublicFooter } from "../components/layout/PublicFooter";

const COLORS = {
  principale: '#210875',
  accent: '#897FCB',
  bg: '#FDFCFF',
  success: '#059669',
  earlybird: '#8a7fce',
};

export const SubscriptionPage = () => {
  const [searchParams] = useSearchParams();
  const promoCode = searchParams.get('promo');
  const planParam = searchParams.get('plan');
  const isEarlyBird = promoCode === 'earlybird';
  const isFlash37 = false;
  const _unused = isFlash37; // conservé pour compat descendante (ignoré)
  const validPlanParams = ['monthly', 'annual', 'earlybird'];
  const preselect = validPlanParams.includes(planParam) ? planParam : null;
  const [selectedPlan, setSelectedPlan] = useState(
    preselect ? preselect : (isEarlyBird ? "earlybird" : "annual")
  );
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [referralCodeInput, setReferralCodeInput] = useState("");
  const [referralStatus, setReferralStatus] = useState(null); // { valid, referrer_prenom } | { valid: false, error }
  const [proConfirmed, setProConfirmed] = useState(false);
  const [showFreeTrialForm, setShowFreeTrialForm] = useState(false);
  const [trialPassword, setTrialPassword] = useState("");
  const [trialPrenom, setTrialPrenom] = useState("");
  const [trialNom, setTrialNom] = useState("");
  const { user, setAuthToken } = useAuth();
  const navigate = useNavigate();

  // Pré-remplir email si utilisateur connecté (renouvellement depuis l'app)
  useEffect(() => {
    if (user?.email && !email) setEmail(user.email);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Capture UTM params pour tracking marketing
  useEffect(() => {
    const utm = {
      utm_source: searchParams.get("utm_source"),
      utm_medium: searchParams.get("utm_medium"),
      utm_campaign: searchParams.get("utm_campaign"),
      utm_content: searchParams.get("utm_content"),
      utm_term: searchParams.get("utm_term"),
    };
    if (Object.values(utm).some(Boolean)) {
      sessionStorage.setItem("biz360_utm", JSON.stringify(utm));
    }
    // Pré-remplir le code parrainage UNIQUEMENT si l'URL contient ?ref=...
    // (sinon, on garde la page de souscription normale, même si un ancien ref traîne en sessionStorage)
    const refFromUrl = searchParams.get("ref");
    if (refFromUrl) {
      setReferralCodeInput(refFromUrl);
      sessionStorage.setItem("pending_referral_code", refFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Valider le code parrainage quand l'utilisateur le saisit (debounced)
  useEffect(() => {
    if (!referralCodeInput || referralCodeInput.length < 3) {
      setReferralStatus(null);
      return;
    }
    const t = setTimeout(() => {
      axios
        .get(`${API}/referral/check/${encodeURIComponent(referralCodeInput.toLowerCase().trim())}`)
        .then((res) => {
          if (res.data.valid) {
            setReferralStatus({ valid: true, referrer_prenom: res.data.referrer_prenom });
            sessionStorage.setItem("pending_referral_code", referralCodeInput.toLowerCase().trim());
            // Auto-ouvre le mini-form si l'utilisateur vient d'un lien de parrainage
            if (searchParams.get("ref")) {
              setShowFreeTrialForm(true);
            }
          } else {
            setReferralStatus({ valid: false });
          }
        })
        .catch(() => setReferralStatus({ valid: false }));
    }, 400);
    return () => clearTimeout(t);
  }, [referralCodeInput]);

  const regularPlans = [
    {
      id: "monthly",
      name: "Mensuel",
      price: 12,
      period: "mois",
      description: "Sans engagement",
      features: [
        "Accès complet à toutes les fonctionnalités",
        "Tableau de bord en temps réel",
        "Calculs Urssaf automatiques (cotisations, CFP, taxes)",
        "Alertes seuils TVA & CFE",
        "TVA intelligente (collectée / déductible / nette)",
        "Suivi ventes & dépenses avec catégorisation",
        "Récap CA mensuel et annuel",
        "Support par email"
      ]
    },
    {
      id: "annual",
      name: "Annuel",
      price: 97,
      period: "an",
      description: "Soit 8,08€/mois — 2 mois offerts",
      savings: "Économisez 33% (vs mensuel)",
      popular: true,
      features: [
        "Tout ce qui est inclus dans l'offre Mensuelle",
        "2 mois offerts (97€/an au lieu de 144€)",
        "Tarif bloqué sur 12 mois",
        "Support prioritaire (réponse sous 24h)"
      ]
    }
  ];

  const earlyBirdPlan = {
    id: "earlybird",
    name: "Early Bird",
    price: 67,
    originalPrice: 97,
    period: "an",
    description: "Offre de lancement exclusive",
    savings: "-30% réservé aux premiers !",
    popular: true,
    features: ["Accès complet à vie", "Calculs Urssaf automatiques", "Alertes TVA/CFE/Retraite", "Support prioritaire", "Mises à jour gratuites", "🎁 Tarif bloqué à vie"]
  };

  const plans = isEarlyBird ? [earlyBirdPlan] : regularPlans;

  const handleSubscribe = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Merci de renseigner un email valide");
      return;
    }
    if (!proConfirmed) {
      toast.error("Merci de confirmer ta qualité de professionnel et l'acceptation des CGV/CGU");
      return;
    }
    setLoading(true);
    try {
      const referralCode = referralStatus?.valid
        ? referralCodeInput.toLowerCase().trim()
        : sessionStorage.getItem("pending_referral_code") || undefined;
      const utm = JSON.parse(sessionStorage.getItem("biz360_utm") || "{}");
      const payload = { plan: selectedPlan, email: email.trim().toLowerCase() };
      payload.pro_confirmed = true;
      payload.terms_accepted_at = new Date().toISOString();
      if (referralCode) payload.referral_code = referralCode;
      if (Object.values(utm).some(Boolean)) payload.utm = utm;
      const response = await axios.post(`${API}/checkout/public-create`, payload);
      if (response.data.checkout_url) {
        // ⭐ Meta Pixel: InitiateCheckout
        const planPrices = { monthly: 12, annual: 97, earlybird: 8.4 };
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'InitiateCheckout', {
            content_name: `Biz360 - ${selectedPlan}`,
            content_category: 'subscription',
            value: planPrices[selectedPlan] || 0,
            currency: 'EUR',
          });
        }
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création du paiement");
      setLoading(false);
    }
  };

  const handleFreeTrialSubmit = async () => {
    if (!email || !email.includes("@")) return toast.error("Email invalide");
    if (!proConfirmed) return toast.error("Merci d'accepter les CGV/CGU");
    if (trialPassword.length < 8) return toast.error("Mot de passe : 8 caractères minimum");
    if (!trialPrenom.trim() || !trialNom.trim()) return toast.error("Prénom et nom requis");
    setLoading(true);
    try {
      const r = await axios.post(`${API}/register/with-referral`, {
        email: email.trim().toLowerCase(),
        password: trialPassword,
        prenom: trialPrenom.trim(),
        nom: trialNom.trim(),
        referral_code: referralCodeInput.toLowerCase().trim(),
        pro_confirmed: true,
      });
      setAuthToken(r.data.token, r.data.user);
      sessionStorage.removeItem("pending_referral_code");
      toast.success(`Bienvenue ! ${r.data.referrer_prenom || "Ton parrain"} t'offre 1 mois gratuit 🎁`);
      navigate("/onboarding");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création du compte");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: COLORS.bg }}>
      {/* Header */}
      <div className="p-6 text-center">
        <Biz360Logo size={48} />

        {referralStatus?.valid && referralStatus.referrer_prenom ? (
          <>
            <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full text-sm font-bold text-white" style={{ backgroundColor: COLORS.success, boxShadow: `0 6px 20px -6px ${COLORS.success}80` }}>
              <Gift size={16} /> +1 MOIS OFFERT
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold mt-5 leading-tight" style={{ color: COLORS.principale }}>
              🎁 {referralStatus.referrer_prenom} t'offre 1 mois gratuit&nbsp;!
            </h1>
            <p className="text-base text-muted-foreground mt-3 max-w-xl mx-auto">
              Découvre Biz360 pendant 30 jours <strong>sans carte bancaire</strong>. Renseigne juste ton email et un mot de passe, on s'occupe du reste.
            </p>
          </>
        ) : isEarlyBird ? (
          <>
            <div className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full text-sm font-medium text-white" style={{ backgroundColor: COLORS.earlybird }}>
              <Gift size={16} /> Offre Early Bird Exclusive
            </div>
            <h1 className="text-2xl font-bold mt-4" style={{ color: COLORS.principale }}>
              Bienvenue dans les premiers ! 🎉
            </h1>
            <p className="text-muted-foreground mt-2">
              Profite de ton tarif exclusif réservé aux Early Birds
            </p>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-bold mt-4" style={{ color: COLORS.principale }}>
              Choisis ta formule
            </h1>
            <p className="text-muted-foreground mt-2">
              Pilote ton auto-entreprise en toute sérénité
            </p>
          </>
        )}
      </div>

      {/* Plans */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-4xl w-full">
          {/* Plans réguliers — masqués en mode parrainage pour ne pas distraire */}
          {!referralStatus?.valid && (
          <div className={`grid gap-6 ${isEarlyBird ? 'grid-cols-1 max-w-md mx-auto' : 'grid-cols-1 md:grid-cols-2'}`}>
            {plans.map((plan) => (
              <div
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`relative cursor-pointer rounded-2xl p-6 transition-all ${
                  selectedPlan === plan.id
                    ? "ring-2 shadow-lg"
                    : "border hover:shadow-md"
                }`}
                style={{
                  backgroundColor: "white",
                  ringColor: selectedPlan === plan.id ? (isEarlyBird ? COLORS.earlybird : COLORS.principale) : undefined,
                  borderColor: "#e5e7eb"
                }}
              >
                {/* Badge */}
                {plan.popular && (
                  <div 
                    className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white flex items-center gap-1"
                    style={{ backgroundColor: isEarlyBird ? COLORS.earlybird : COLORS.principale }}
                  >
                    {isEarlyBird ? <><Gift size={12} /> -30% EARLY BIRD</> : <><Sparkles size={12} /> POPULAIRE</>}
                  </div>
                )}

                {/* Radio */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold" style={{ color: isEarlyBird ? COLORS.earlybird : COLORS.principale }}>
                      {plan.name}
                    </h3>
                    <p className="text-sm text-muted-foreground">{plan.description}</p>
                  </div>
                  <div 
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      selectedPlan === plan.id ? "border-2" : "border-gray-300"
                    }`}
                    style={{ borderColor: selectedPlan === plan.id ? COLORS.principale : undefined }}
                  >
                    {selectedPlan === plan.id && (
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS.principale }} />
                    )}
                  </div>
                </div>

                {/* Prix */}
                <div className="mb-4">
                  {plan.originalPrice && (
                    <span className="text-xl text-muted-foreground line-through mr-2">
                      {plan.originalPrice}€
                    </span>
                  )}
                  <span className="text-4xl font-bold" style={{ color: isEarlyBird ? COLORS.earlybird : COLORS.principale }}>
                    {plan.price}€
                  </span>
                  <span className="text-muted-foreground">/{plan.period}</span>
                  {plan.savings && (
                    <span 
                      className="ml-2 px-2 py-1 rounded-full text-xs font-medium"
                      style={{ backgroundColor: COLORS.success + '20', color: COLORS.success }}
                    >
                      {plan.savings}
                    </span>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-2">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm">
                      <Check size={16} style={{ color: COLORS.success }} />
                      {feature}
                    </li>
                ))}
              </ul>
            </div>
          ))}
          </div>
          )}
        </div>
      </div>

      {/* CTA */}
      <div className="p-6 max-w-3xl mx-auto w-full">
        {/* Email input */}
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2" style={{ color: COLORS.principale }}>
            Ton email
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="toi@exemple.com"
            data-testid="subscribe-email-input"
            className="w-full h-12 px-4 rounded-xl border-2 border-gray-200 bg-white focus:outline-none focus:border-[#897FCB] transition-colors"
            required
          />
          <p className="text-xs text-muted-foreground mt-2">
            {referralStatus?.valid
              ? "Ton compte est créé instantanément. Aucune carte bancaire ni paiement."
              : "Tu créeras ton compte juste après le paiement (prend 30 secondes)."}
          </p>
        </div>

        {/* Code parrainage — masqué en mode parrainage (le hero + form gratuit suffisent) */}
        {!referralStatus?.valid && (
        <div className="mb-4" data-testid="referral-section">
          <div
            className="relative rounded-2xl p-5 overflow-hidden border-2 transition-all"
            style={{
              borderColor: referralStatus?.valid === true ? COLORS.success : (referralCodeInput ? '#DDD6FE' : '#E9E5FA'),
              background: referralStatus?.valid === true
                ? `linear-gradient(135deg, ${COLORS.success}10 0%, ${COLORS.success}05 100%)`
                : 'linear-gradient(135deg, #F4F1FF 0%, #FDFCFF 100%)',
            }}
          >
            {/* Décoration en fond */}
            <div
              aria-hidden="true"
              className="absolute -top-8 -right-8 w-32 h-32 rounded-full opacity-20"
              style={{ background: `radial-gradient(circle, ${COLORS.accent} 0%, transparent 70%)` }}
            />

            <div className="relative flex items-start gap-4">
              <div
                className="flex-shrink-0 w-12 h-12 rounded-2xl flex items-center justify-center text-2xl"
                style={{
                  backgroundColor: referralStatus?.valid === true ? COLORS.success : COLORS.accent,
                  boxShadow: `0 8px 24px -8px ${referralStatus?.valid === true ? COLORS.success : COLORS.accent}80`,
                }}
              >
                <Gift size={22} color="white" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="font-bold text-base" style={{ color: COLORS.principale }}>
                    Un(e) ami(e) te parraine ?
                  </h4>
                  <span
                    className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                    style={{ backgroundColor: COLORS.accent + '25', color: COLORS.principale }}
                  >
                    +1 mois gratuit
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Saisis son code parrainage ci-dessous, ton mois offert s'ajoutera automatiquement à ton abonnement 🎁
                </p>

                <div className="relative">
                  <input
                    type="text"
                    value={referralCodeInput}
                    onChange={(e) => setReferralCodeInput(e.target.value)}
                    placeholder="ex : mariebiz"
                    data-testid="referral-code-input"
                    autoComplete="off"
                    className="w-full h-11 px-4 pr-12 rounded-xl border-2 bg-white text-sm font-medium focus:outline-none transition-colors placeholder:font-normal placeholder:text-gray-400"
                    style={{
                      borderColor:
                        referralStatus?.valid === true
                          ? COLORS.success
                          : referralStatus?.valid === false && referralCodeInput.length >= 3
                          ? '#DC2626'
                          : '#E5E7EB',
                    }}
                  />
                  {referralStatus?.valid === true && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Check size={20} style={{ color: COLORS.success }} />
                    </div>
                  )}
                </div>

                {referralStatus?.valid === true && (
                  <div
                    className="mt-3 p-3 rounded-lg flex items-start gap-2"
                    style={{ backgroundColor: COLORS.success + '15' }}
                    data-testid="referral-valid"
                  >
                    <Sparkles size={16} style={{ color: COLORS.success }} className="mt-0.5 flex-shrink-0" />
                    <p className="text-xs font-medium" style={{ color: COLORS.success }}>
                      Code valide{referralStatus.referrer_prenom ? ` — ${referralStatus.referrer_prenom} t'offre 1 mois supplémentaire 🎉` : ' — ton mois cadeau est réservé 🎉'}
                    </p>
                  </div>
                )}
                {referralStatus?.valid === false && referralCodeInput.length >= 3 && (
                  <p className="text-xs mt-2 font-medium" style={{ color: '#DC2626' }} data-testid="referral-invalid">
                    Code inconnu — vérifie l'orthographe (pas de majuscule ni d'espace)
                  </p>
                )}
                {!referralCodeInput && (
                  <p className="text-[11px] text-muted-foreground/70 mt-2">
                    Pas de code ? Aucun souci, tu peux quand même profiter de l'offre 👌
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
        )}

        {/* ── Case à cocher : confirmation professionnel + acceptation CGU/CGV ── */}
        <label
          className="flex items-start gap-3 mb-4 p-4 rounded-xl border bg-white cursor-pointer hover:border-primary/40 transition"
          style={{ borderColor: proConfirmed ? COLORS.principale : '#E5E7EB' }}
          data-testid="pro-confirm-wrapper"
        >
          <input
            type="checkbox"
            checked={proConfirmed}
            onChange={(e) => setProConfirmed(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-[#210875] cursor-pointer flex-shrink-0"
            data-testid="pro-confirm-checkbox"
          />
          <span className="text-xs leading-relaxed text-muted-foreground">
            Je déclare <strong className="text-foreground">agir en qualité de professionnel</strong>{" "}
            (auto-entrepreneur, micro-entrepreneur ou autre activité économique indépendante)
            et reconnais que le service Biz360 est exclusivement réservé aux professionnels.
            J'accepte sans réserve les{" "}
            <a
              href="/cgv"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              style={{ color: COLORS.principale }}
            >
              Conditions Générales de Vente
            </a>{" "}
            et les{" "}
            <a
              href="/mentions-legales-cgu#cgu"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              style={{ color: COLORS.principale }}
            >
              Conditions Générales d'Utilisation
            </a>
            .
          </span>
        </label>

        {/* Si le code parrainage est valide, on affiche le mini-form gratuit */}
        {referralStatus?.valid === true && !showFreeTrialForm && (
          <button
            onClick={() => setShowFreeTrialForm(true)}
            disabled={!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) || !proConfirmed}
            className="w-full h-14 rounded-xl text-white font-bold text-lg flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
            style={{ backgroundColor: COLORS.success }}
            data-testid="claim-free-trial-btn"
          >
            <Gift size={20} /> Créer mon compte gratuit (1 mois offert)
          </button>
        )}

        {referralStatus?.valid === true && showFreeTrialForm && (
          <div className="p-5 rounded-2xl border-2 space-y-3" style={{borderColor: COLORS.success, backgroundColor: COLORS.success + "08"}} data-testid="free-trial-form">
            <div className="font-bold" style={{color: COLORS.success}}>✨ Un dernier détail pour créer ton compte</div>
            <div className="grid grid-cols-2 gap-3">
              <input
                type="text"
                value={trialPrenom}
                onChange={(e) => setTrialPrenom(e.target.value)}
                placeholder="Ton prénom"
                className="h-11 px-3 rounded-lg border-2 bg-white text-sm focus:outline-none"
                style={{borderColor: '#E5E7EB'}}
                data-testid="free-trial-prenom"
              />
              <input
                type="text"
                value={trialNom}
                onChange={(e) => setTrialNom(e.target.value)}
                placeholder="Ton nom"
                className="h-11 px-3 rounded-lg border-2 bg-white text-sm focus:outline-none"
                style={{borderColor: '#E5E7EB'}}
                data-testid="free-trial-nom"
              />
            </div>
            <input
              type="password"
              value={trialPassword}
              onChange={(e) => setTrialPassword(e.target.value)}
              placeholder="Choisis un mot de passe (min. 8 caractères)"
              className="w-full h-11 px-3 rounded-lg border-2 bg-white text-sm focus:outline-none"
              style={{borderColor: '#E5E7EB'}}
              data-testid="free-trial-password"
            />
            <button
              onClick={handleFreeTrialSubmit}
              disabled={loading || trialPassword.length < 8 || !trialPrenom || !trialNom}
              className="w-full h-12 rounded-xl text-white font-bold flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
              style={{ backgroundColor: COLORS.success }}
              data-testid="free-trial-submit"
            >
              {loading ? (
                <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <>🎁 Activer mon mois gratuit</>
              )}
            </button>
            <p className="text-xs text-center text-muted-foreground">
              Aucune carte bancaire demandée. Ton mois offert démarre immédiatement.
            </p>
          </div>
        )}

        {!referralStatus?.valid && (
          <button
            onClick={handleSubscribe}
            disabled={loading || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email) || !proConfirmed}
            className="w-full h-14 rounded-xl text-white font-bold text-lg flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
            style={{ backgroundColor: COLORS.principale }}
            data-testid="subscribe-btn"
          >
            {loading ? (
              <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <>
                <CreditCard size={20} />
                Continuer vers le paiement
              </>
            )}
          </button>
        )}

        <div className="flex items-center justify-center gap-6 mt-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Shield size={14} /> Paiement sécurisé
          </span>
          <span className="flex items-center gap-1">
            <Calendar size={14} /> Annulation par email
          </span>
        </div>
      </div>

      <PublicFooter />
    </div>
  );
};
